import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { createPortal } from "react-dom";
import { useDebouncedValue } from "../../../../hooks/useDebounce";

const ListSkeleton = ({ rows = 6 }) => (
  <div className="mt-3 space-y-3 px-4">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="animate-pulse rounded-lg bg-white p-3 shadow">
        <div className="mb-2 h-4 w-1/3 rounded bg-gray-200/80" />
        <div className="space-y-2">
          <div className="h-4 w-5/6 rounded bg-gray-200/80" />
          <div className="h-4 w-3/5 rounded bg-gray-200/80" />
        </div>
      </div>
    ))}
  </div>
);

const backdrop = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.15 } },
  exit: { opacity: 0, transition: { duration: 0.12 } },
};

const sheet = {
  hidden: { y: "100%" },
  show: {
    y: 0,
    transition: { type: "spring", stiffness: 420, damping: 40, mass: 0.8 },
  },
  exit: { y: "100%", transition: { duration: 0.18 } },
};

/* ---------------- component ---------------- */
const ServicePopup = ({ data, category, onClose, serviceLoading }) => {
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debounced = useDebouncedValue(searchTerm, 250);

  /* PORTAL GUARD */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  /* sync data */
  useEffect(() => {
    setServices(Array.isArray(data) ? data : []);
  }, [data]);

  /* lock body scroll */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  /* ESC close */
  const onKey = useCallback(
    (e) => {
      if (e.key === "Escape") onClose?.();
    },
    [onClose]
  );

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onKey]);

  /* filter */
  const filteredServices = useMemo(() => {
    const q = (debounced || "").trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => (s.name || "").toLowerCase().includes(q));
  }, [debounced, services]);

  const handleDragEnd = (_e, info) => {
    if (info.offset.y > 120 || info.velocity.y > 700) {
      onClose?.();
    }
  };

  const go = (service) => {
    navigate(`/danh-sach-dich-vu/dich-vu/${service.formName}/${service.id}`);
  };

  /* 🚫 CHƯA MOUNT → KHÔNG RENDER PORTAL */
  if (!mounted) return null;

  /* ✅ PORTAL – ĐÈ TOÀN APP */
  return createPortal(
    <motion.div
      className="fixed inset-0 z-[2000] flex items-end justify-center bg-black/50"
      variants={backdrop}
      initial="hidden"
      animate="show"
      exit="exit"
      onClick={onClose}
    >
      {/* OUTER */}
      <div
        className="
          w-full bg-[#F2F2F2]
          h-[100svh] md:h-[100vh]
          flex flex-col
        "
        onClick={(e) => e.stopPropagation()}
      >
        {/* SHEET */}
        <motion.div
          className="flex h-full flex-col"
          role="dialog"
          aria-modal="true"
          aria-label={`Dịch vụ - ${category?.name ?? "Danh mục"}`}
          variants={sheet}
          initial="hidden"
          animate="show"
          exit="exit"
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ bottom: 0.6 }}
          onDragEnd={handleDragEnd}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3">
            <h2 className="line-clamp-1 text-lg font-bold">
              {category?.name || "Danh mục"}
            </h2>
            <button
              onClick={onClose}
              className="rounded p-1 focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              <img src="/icons/x-close.svg" alt="" className="h-5 w-5" />
            </button>
          </div>

          {/* Search */}
          <div className="relative px-4 my-3">
            <input
              type="text"
              placeholder="Bạn đang muốn tìm kiếm..."
              className="w-full rounded-3xl border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-blue-300"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="absolute right-6 top-1/2 -translate-y-1/2">
              <img src="/icons/search-black.svg" alt="" />
            </span>
          </div>

          {/* CONTENT */}
          <div className="flex-1 overflow-y-auto pb-safe">
            {serviceLoading ? (
              <ListSkeleton rows={6} />
            ) : filteredServices.length ? (
              <div className="space-y-3 px-4 pb-4">
                {filteredServices.map((service) => (
                  <motion.button
                    key={service.id}
                    type="button"
                    onClick={() => go(service)}
                    whileTap={{ scale: 0.98 }}
                    className="
                      group relative w-full overflow-hidden rounded-xl
                      border border-gray-200/80 bg-white p-4
                      shadow-sm transition-all
                      hover:border-blue-300 hover:shadow-md
                    "
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-lg bg-blue-50 p-2">
                        <img
                          src="/images/default-form.png"
                          alt=""
                          className="h-full w-full object-contain"
                        />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h3 className="line-clamp-2 text-base text-left font-medium text-gray-800 group-hover:text-blue-600">
                          {service.name}
                        </h3>
                        <div className="mt-2 space-y-1 text-left text-sm text-gray-600 md:hidden">
                          <p>
                            <span className="font-medium text-gray-700">
                              Nộp đơn:
                            </span>{" "}
                            {service.submissionTime || "--"}
                          </p>
                          <p>
                            <span className="font-medium text-gray-700">
                              Thẩm định:
                            </span>{" "}
                            {service.reviewTime || "--"}
                          </p>
                        </div>
                      </div>

                      <div className="h-8 w-8 flex items-center justify-center rounded-full bg-gray-50">
                        <img
                          src="/icons/right-arrow-black.svg"
                          alt=""
                          className="h-4 w-4"
                        />
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <p className="text-base font-medium text-gray-700">
                  Không tìm thấy kết quả
                </p>
                <p className="text-sm text-gray-500">
                  Vui lòng thử từ khóa khác
                </p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>,
    document.body
  );
};

export default ServicePopup;
